import axios from 'axios';

const BASE_API_URL = 'https://rest.lilybharathindia.com/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// Authentication
export const login = async (credentials) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/auth/login`, credentials);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to login');
  }
};

export const register = async (userData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/auth/register`, userData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to register');
  }
};

export const verifyEmail = async (token, id) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/auth/verify-email?token=${token}&id=${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Verification failed');
  }
};

export const verifyToken = async () => {
  try {
    const response = await axios.get(`${BASE_API_URL}/auth/profile`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to verify token');
  }
};

export const getAllUsers = async () => {
  try {
    const response = await axios.get(`${BASE_API_URL}/auth/users`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch users');
  }
};

// Companies
export const listCompanies = async () => {
  try {
    const response = await axios.get(`${BASE_API_URL}/companies`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch companies');
  }
};

export const getCompanyById = async (id) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/companies/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch company by ID');
  }
};

export const createCompany = async (companyData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/companies`, companyData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create company');
  }
};

export const updateCompany = async (id, companyData) => {
  try {
    const response = await axios.put(`${BASE_API_URL}/companies/${id}`, companyData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update company');
  }
};

export const deleteCompany = async (id) => {
  try {
    const response = await axios.delete(`${BASE_API_URL}/companies/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete company');
  }
};

// Insurance Companies
export const listInsCompanies = async () => {
  try {
    const response = await axios.get(`${BASE_API_URL}/ins-companies`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch insurance companies');
  }
};

export const getInsCompanyById = async (id) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/ins-companies/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch insurance company by ID');
  }
};

export const createInsCompany = async (insCompanyData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/ins-companies`, insCompanyData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create insurance company');
  }
};

export const updateInsCompany = async (id, insCompanyData) => {
  try {
    const response = await axios.put(`${BASE_API_URL}/ins-companies/${id}`, insCompanyData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update insurance company');
  }
};

export const deleteInsCompany = async (id) => {
  try {
    const response = await axios.delete(`${BASE_API_URL}/ins-companies/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete insurance company');
  }
};

// Agents
export const listAgents = async () => {
  try {
    const response = await axios.get(`${BASE_API_URL}/agents`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch agents');
  }
};

export const getAgentById = async (id) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/agents/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch agent by ID');
  }
};

export const createAgent = async (agentData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/agents`, agentData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create agent');
  }
};

export const updateAgent = async (id, agentData) => {
  try {
    const response = await axios.put(`${BASE_API_URL}/agents/${id}`, agentData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update agent');
  }
};

export const deleteAgent = async (id) => {
  try {
    const response = await axios.delete(`${BASE_API_URL}/agents/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete agent');
  }
};

// Risk Items
export const listRiskItems = async () => {
  try {
    const response = await axios.get(`${BASE_API_URL}/risk-items`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch risk items');
  }
};

export const getRiskItemById = async (id) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/risk-items/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch risk item by ID');
  }
};

export const createRiskItem = async (riskItemData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/risk-items`, riskItemData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create risk item');
  }
};

export const updateRiskItem = async (id, riskItemData) => {
  try {
    const response = await axios.put(`${BASE_API_URL}/risk-items/${id}`, riskItemData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update risk item');
  }
};

export const deleteRiskItem = async (id) => {
  try {
    const response = await axios.delete(`${BASE_API_URL}/risk-items/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete risk item');
  }
};

// Bankers
export const listBankers = async () => {
  try {
    const response = await axios.get(`${BASE_API_URL}/bankers`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch bankers');
  }
};

export const getBankerById = async (id) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/bankers/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch banker by ID');
  }
};

export const createBanker = async (bankerData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/bankers`, bankerData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create banker');
  }
};

export const updateBanker = async (id, bankerData) => {
  try {
    const response = await axios.put(`${BASE_API_URL}/bankers/${id}`, bankerData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update banker');
  }
};

export const deleteBanker = async (id) => {
  try {
    const response = await axios.delete(`${BASE_API_URL}/bankers/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete banker');
  }
};

// Utility Bills
export const createBill = async (billData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/utility-bills`, billData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create utility bill');
  }
};

export const deleteBill = async (id) => {
  try {
    const response = await axios.delete(`${BASE_API_URL}/utility-bills/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete utility bill');
  }
};

export const listBills = async () => {
  try {
    const response = await axios.get(`${BASE_API_URL}/utility-bills`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch utility bills');
  }
};

export const updateBill = async (id, billData) => {
  try {
    const response = await axios.put(`${BASE_API_URL}/utility-bills/${id}`, billData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update utility bill');
  }
};

export const getBillById = async (id) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/utility-bills/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch bill by ID');
  }
};

// Loans
export const createLoan = async (loanData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/loan-payments`, loanData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create loan');
  }
};

export const deleteLoan = async (id) => {
  try {
    const response = await axios.delete(`${BASE_API_URL}/loan-payments/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete loan');
  }
};

export const listLoans = async () => {
  try {
    const response = await axios.get(`${BASE_API_URL}/loan-payments`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch loans');
  }
};

export const updateLoan = async (id, loanData) => {
  try {
    const response = await axios.put(`${BASE_API_URL}/loan-payments/${id}`, loanData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update loan');
  }
};

export const getLoanById = async (id) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/loan-payments/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch loan by ID');
  }
};

// Rents
export const createRent = async (rentData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/rent-expenses`, rentData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create rent');
  }
};

export const deleteRent = async (id) => {
  try {
    const response = await axios.delete(`${BASE_API_URL}/rent-expenses/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete rent');
  }
};

export const listRents = async () => {
  try {
    const response = await axios.get(`${BASE_API_URL}/rent-expenses`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch rents');
  }
};

export const updateRent = async (id, rentData) => {
  try {
    const response = await axios.put(`${BASE_API_URL}/rent-expenses/${id}`, rentData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update rent');
  }
};

export const getRentById = async (id) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/rent-expenses/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch rent by ID');
  }
};

// Insurances
export const listInsurances = async () => {
  try {
    const response = await axios.get(`${BASE_API_URL}/insurance-renewals`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch insurances');
  }
};

export const createInsurance = async (insuranceData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/insurance-renewals`, insuranceData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create insurance');
  }
};

export const deleteInsurance = async (id) => {
  try {
    const response = await axios.delete(`${BASE_API_URL}/insurance-renewals/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete insurance');
  }
};

export const updateInsurance = async (id, insuranceData) => {
  try {
    const response = await axios.put(`${BASE_API_URL}/insurance-renewals/${id}`, insuranceData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update insurance');
  }
};

export const getInsuranceById = async (id) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/insurance-renewals/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch insurance by ID');
  }
};

// Reminders
export const createReminder = async (reminderData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/reminders`, reminderData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create reminder');
  }
};

export const setReminder = async (recordId, recordType, days) => {
  try {
    const response = await axios.post(
      `${BASE_API_URL}/reminders`,
      {
        recordId,
        recordType,
        reminderDays: days
      },
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to set reminder');
  }
};

export const listReminders = async () => {
  try {
    const response = await axios.get(`${BASE_API_URL}/reminders`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch reminders');
  }
};

// Client-side filtering for reminders
export const getUtilityReminders = async () => {
  try {
    const bills = await listBills();
    const today = new Date();
    return bills.filter((bill) => {
      const dueDate = new Date(bill.due_date);
      const diffDays = (dueDate - today) / (1000 * 60 * 60 * 24);
      return diffDays <= 30 && diffDays >= 0;
    });
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch utility reminders');
  }
};

export const getInsuranceReminders = async () => {
  try {
    const insurances = await listInsurances();
    const today = new Date();
    return insurances.filter((item) => {
      const dueDate = new Date(item.policy_date);
      const diffDays = (dueDate - today) / (1000 * 60 * 60 * 24);
      return diffDays <= 30 && diffDays >= 0;
    });
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch insurance reminders');
  }
};

export const getLoanReminders = async () => {
  try {
    const loans = await listLoans();
    const today = new Date();
    return loans.filter((loan) => {
      const dueDate = new Date(loan.emi_date);
      const diffDays = (dueDate - today) / (1000 * 60 * 60 * 24);
      return diffDays <= 30 && diffDays >= 0;
    });
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch loan reminders');
  }
};

export const getRentReminders = async () => {
  try {
    const rents = await listRents();
    const today = new Date();
    return rents.filter((rent) => {
      const dueDate = new Date(rent.payment_due_date);
      const diffDays = (dueDate - today) / (1000 * 60 * 60 * 24);
      return diffDays <= 30 && diffDays >= 0;
    });
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch rent reminders');
  }
};

// Stats
export const getStats = async () => {
  try {
    const response = await axios.get(`${BASE_API_URL}/stats`, {
      headers: getAuthHeaders(),
    });
    return {
      total_users: response.data.userCount,
      total_companies: response.data.companyCount,
      total_ins_companies: response.data.insCompanyCount,
      total_agents: response.data.agentCount,
      total_risk_items: response.data.riskItemCount,
      total_bankers: response.data.bankerCount,
      total_insurances: response.data.insuranceCount,
      total_loans: response.data.loanCount,
      total_rents: response.data.rentCount,
      total_bills: response.data.utilityCount,
    };
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch stats');
  }
};

// Health Check
export const checkHealth = async () => {
  try {
    const response = await axios.get(`${BASE_API_URL}/health`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to check server health');
  }
};

export { BASE_API_URL };